import React from "react";
import clsx from "classnames";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { makeStyles } from "@material-ui/styles";

import { ExpandMore } from "@material-ui/icons";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  Typography,
  Divider,
  ListItem,
  ListItemText,
  List as MuiList
} from "@material-ui/core";

import { translate } from "jazasoft";

import MuiTable from "../../../components/MuiTable";

import ResourceKeys from "../../../utils/ResourceKeys";
import { useDeepMemo, useQuery, useSafeState, useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { getDistinctValues, isEmpty } from "../../../utils/helpers";
import useWarehouse from "../../../hooks/useWarehouse";

const getUpdateText = (field, fromValue, toValue) => {
  return (
    <div>
      <span style={{ fontWeight: "bold" }}>{field}</span> changed from '
      <span style={{ fontStyle: "italic", textDecoration: "underline" }}>{fromValue}</span> ' to '
      <span style={{ fontStyle: "italic", textDecoration: "underline" }}>{toValue}</span> '
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "#fafafa",
    flex: 1
  },

  accordionDetail: {
    display: "flex",
    flexDirection: "column",
    width: 1080,
    padding: 0
  },
  padding: {
    padding: "1em"
  }
}));

const iColumns = [
  { dataKey: "id", title: "Id" },
  { dataKey: "invoiceNo", title: "Invoice No" },
  { dataKey: "erpInvoiceNo", title: "ERP Invoice No" },
  { dataKey: "supplierName", title: "Supplier" }
];

const fabricItemColumns = [
  { dataKey: "id", title: "Id" },
  { dataKey: "itemCode", title: "Item Code" },
  { dataKey: "color", title: "Fabric Color" },
  { dataKey: "colorShade", title: "Color Shade" },
  { dataKey: "fpo", title: "FPO" },
  { dataKey: "orderQty", title: "Fab. Order Qty" },
  { dataKey: "invoiceQty", title: "Invoice Qty" },
  { dataKey: "grn", title: "GRN" },
  { dataKey: "grnDate", title: "GRN Date" },
  { dataKey: "grnQty", title: "GRN Qty" },
  { dataKey: "rollCount", title: "Roll Count" },
  { dataKey: "blanketQty", title: "Blanket Qty \nper Roll" },
  { dataKey: "orderingWidth", title: "Booking Width" },
  { dataKey: "orderingGsm", title: "Booking GSM" },
  { dataKey: "orderingWarpShrinkage", title: "Booking Max. \nWarp Shrinkage" },
  { dataKey: "orderingWeftShrinkage", title: "Booking Max. \nWeft Shrinkage" },
  { dataKey: "warehouse", title: "Warehouse" }
];

const InvoiceDetails = ({ userActivity, translate }) => {
  const classes = useStyles();
  const { allWhList } = useWarehouse();

  const prevDataObject = JSON.parse(userActivity?.prevValue || "{}");
  const currDataObject = JSON.parse(userActivity?.currValue || "{}");

  const [expanded, setExpanded] = useSafeState(false);

  const resource = ResourceKeys[userActivity?.entityName];

  const { data: childActivityList = [] } = useQuery({
    url: "system-activities",
    params: { search: `parentId==${userActivity?.id}` }
  });

  const fabricItemActivities = useDeepMemo(() => {
    const fabricItemList = JSON.parse(userActivity?.resBody || "{}")?.fabricItemList;

    if (userActivity?.method === "DELETE") {
      return fabricItemList?.map(fabricItem => {
        return {
          id: fabricItem?.id,
          entityId: fabricItem?.id,
          operation: "delete",
          prevValue: JSON.stringify(fabricItem)
        };
      });
    }
    const fabricItemIds = childActivityList?.filter(fa => fa.entityName === "FabricItem").map(e => e.entityId);

    const uniqueFlowIds = getDistinctValues([...fabricItemIds]);
    const fabricItemActivityList = uniqueFlowIds?.map(fabricItemId => {
      const activity = childActivityList.find(a => a.entityId === fabricItemId);
      if (!isEmpty(activity))
        return {
          ...activity
        };

      const fabricItem = fabricItemList?.find(fi => fi.id === fabricItemId);
      return {
        id: fabricItem?.id,
        entityId: fabricItem?.id,
        operation: "update"
      };
    });

    return fabricItemActivityList;
  }, [childActivityList, userActivity]);

  //TODO: change the logic when all settings are updated.
  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  const fabItemFields = useSetting(SettingKeys.ENTITY_FIELDS, "fabricItems");
  const fabricItemFields = allWhList.length !== 1 ? [...fabItemFields, "warehouse"] : fabItemFields;

  const invoiceColumns = useDeepMemo(() => {
    if (userActivity?.entityName !== "Invoice" || isEmpty(invoiceFields)) return [];

    let columns = iColumns.map(col => {
      return {
        dataKey: col.dataKey,
        title: translate(`resources.${resource}.fields.${col.dataKey}`, { _: col.title })
      };
    });

    columns = columns.filter(col => invoiceFields.includes(col.dataKey));
    return columns;
  }, [invoiceFields, resource, prevDataObject, currDataObject, userActivity, iColumns]);

  const fabItemColumns = useDeepMemo(() => {
    if (isEmpty(fabricItemFields)) return [];

    let columns = fabricItemColumns.map(col => {
      return {
        dataKey: col.dataKey,
        title: translate(`resources.${resource}.fields.${col.dataKey}`, { _: col.title })
      };
    });

    columns = columns.filter(col => fabricItemFields.includes(col.dataKey));

    return columns;
  }, [fabricItemFields, fabricItemColumns]);

  return (
    <PerfectScrollbar className={classes.root}>
      <div style={{ padding: 16 }}>
        <Typography style={{ marginBottom: 12, color: "#666666" }} variant="h6">
          Invoice Summary
        </Typography>
        <Paper>
          {(userActivity.operation === "write" || userActivity.operation === "delete") && (
            <MuiTable
              columns={invoiceColumns}
              rows={
                userActivity.operation === "write"
                  ? [
                      {
                        ...currDataObject,
                        ...invoiceColumns.reduce(
                          (acc, col) => ({
                            ...acc,
                            [col.dataKey]: !isEmpty(currDataObject[col.dataKey]) ? currDataObject[col.dataKey] : "-"
                          }),
                          {}
                        )
                      }
                    ]
                  : [
                      {
                        ...prevDataObject,
                        ...invoiceColumns.reduce(
                          (acc, col) => ({
                            ...acc,
                            [col.dataKey]: !isEmpty(prevDataObject[col.dataKey]) ? prevDataObject[col.dataKey] : "-"
                          }),
                          {}
                        )
                      }
                    ]
              }
            />
          )}
          {userActivity?.operation === "update" && (
            <MuiList disablePadding>
              {["id", ...invoiceFields, "desc"]
                ?.filter(
                  field =>
                    Object.keys(currDataObject).includes(field) && !isEmpty(currDataObject[field]) && currDataObject[field] !== prevDataObject[field]
                )
                ?.map((field, idx) => {
                  const translatedText = `${translate(`resources.${resource}.fields.${field}`, { _: field })}`;
                  const styledText = getUpdateText(translatedText, prevDataObject[field], currDataObject[field]);
                  return (
                    <ListItem key={idx} divider>
                      <ListItemText primary={styledText} />
                    </ListItem>
                  );
                })}
              {["id", ...invoiceFields, "desc"]?.filter(
                field =>
                  Object.keys(currDataObject).includes(field) && !isEmpty(currDataObject[field]) && currDataObject[field] !== prevDataObject[field]
              )?.length === 0 && (
                <ListItem divider>
                  <ListItemText primary={"No changes in Invoice Summary"} />
                </ListItem>
              )}
            </MuiList>
          )}
        </Paper>
      </div>

      <div style={{ padding: 16 }}>
        <Typography style={{ marginBottom: 8, color: "#666666" }} variant="h6">
          Invoice Details
        </Typography>
        <div>
          {!isEmpty(fabricItemActivities) &&
            fabricItemActivities?.map((fia, idx) => {
              const prevFabricItemDataObject = JSON.parse(fia?.prevValue || "{}");
              const currFabricItemDataObject = JSON.parse(fia?.currValue || "{}");

              return (
                <Accordion
                  key={idx}
                  expanded={expanded === fia?.id}
                  onChange={() => setExpanded(expanded === false ? fia?.id : expanded === fia?.id ? false : fia?.id)}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" style={{ flex: 3, color: "#666666" }}>
                      {`Invoice Detail #${fia.id}`}
                    </Typography>
                    <Typography variant="h6" style={{ textAlign: "right", flex: 1 }}>
                      {fia?.operation === "write" ? "Added" : fia?.operation === "update" ? "Modified" : "Deleted"}
                    </Typography>
                  </AccordionSummary>
                  <Divider />
                  <AccordionDetails className={clsx(classes.accordionDetail)}>
                    {(fia?.operation === "write" || fia?.operation === "delete") && (
                      <MuiTable
                        columns={fabItemColumns}
                        rows={
                          fia?.operation === "write"
                            ? [
                                {
                                  ...currFabricItemDataObject,
                                  ...fabricItemColumns.reduce(
                                    (acc, col) => ({
                                      ...acc,
                                      [col.dataKey]: !isEmpty(currFabricItemDataObject[col.dataKey]) ? currFabricItemDataObject[col.dataKey] : "-"
                                    }),
                                    {}
                                  )
                                }
                              ]
                            : [
                                {
                                  ...prevFabricItemDataObject,
                                  ...fabricItemColumns.reduce(
                                    (acc, col) => ({
                                      ...acc,
                                      [col.dataKey]: !isEmpty(prevFabricItemDataObject[col.dataKey]) ? prevFabricItemDataObject[col.dataKey] : "-"
                                    }),
                                    {}
                                  )
                                }
                              ]
                        }
                      />
                    )}
                    {fia?.operation === "update" && (
                      <Paper>
                        <MuiList disablePadding>
                          {fabricItemFields
                            ?.filter(field => currFabricItemDataObject[field] !== prevFabricItemDataObject[field])
                            ?.map((field, idx) => {
                              const translatedText = `${translate(`resources.${resource}.fields.${field}`, { _: field })}`;
                              const styledText = getUpdateText(translatedText, prevFabricItemDataObject[field], currFabricItemDataObject[field]);
                              return (
                                <ListItem key={idx} divider>
                                  <ListItemText primary={styledText} />
                                </ListItem>
                              );
                            })}
                          {fabricItemFields?.filter(field => currFabricItemDataObject[field] !== prevFabricItemDataObject[field])?.length === 0 && (
                            <ListItem divider>
                              <ListItemText primary={"No changes in Invoice Details"} />
                            </ListItem>
                          )}
                        </MuiList>
                      </Paper>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </div>
      </div>
    </PerfectScrollbar>
  );
};

export default translate(InvoiceDetails);
